import React from 'react';
import { useTranslation } from 'react-i18next';
import { Wysiwyg } from '@cogneed/design-system';
import HoverExplanationTooltip from '../../../../Common/HoverExplanationTooltip';
import { SessionComplianceChecklistItem } from '../../../LiveContext/NewSessionContext/Contexts/SessionComplianceContextProvider/type';
import CommandChips from '../CommandChips';
import StyledBox from './styles';

interface ComplianceItemContentProps {
  item: SessionComplianceChecklistItem;
  density: 'panel' | 'page';
}

// The body shared by a pending item's card (ComplianceItemCard) and a
// processed item's expanded row (ComplianceProcessedGroup): validation
// progress + command chips + instructions. Only the instructions' clamping
// (panel: 3 lines + "Voir tout"; page: full text) depends on density.
export default function ComplianceItemContent(props: ComplianceItemContentProps) {
  const { item, density } = props;
  const { t } = useTranslation();
  const [instructionsExpanded, setInstructionsExpanded] = React.useState(false);
  const [isInstructionsOverflowing, setIsInstructionsOverflowing] = React.useState(false);
  const instructionsBodyRef = React.useRef<HTMLDivElement>(null);
  const detectedValidatingCount = item.validatingCommands.filter((command) => command.isDetected).length;

  // Only worth measuring while the clamp is actually active -- once expanded
  // there's nothing to overflow, and re-measuring then would wrongly hide
  // the toggle (no way back to "Voir moins").
  React.useLayoutEffect(() => {
    if (density !== 'panel' || instructionsExpanded) return undefined;
    const el = instructionsBodyRef.current;
    if (!el) return undefined;
    const measure = () => setIsInstructionsOverflowing(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [density, instructionsExpanded, item.complianceExample]);

  return (
    <StyledBox density={density}>
      {item.validatingCommands.length > 0 && (
        <div className="complianceRow">
          <span className="complianceRowLabel">
            {t('home-page.compliance.mandatory.commands-validation.title', { detected: detectedValidatingCount, total: item.projectTopic.mentionThreshold })}
            <HoverExplanationTooltip popoverId="commands-validation-tooltip">
              {t('home-page.compliance.mandatory.commands-validation.tooltip', { title: item.topic.text, total: item.projectTopic.mentionThreshold })}
            </HoverExplanationTooltip>
          </span>
          <CommandChips commands={item.validatingCommands} />
        </div>
      )}

      {item.rejectingCommands.length > 0 && (
        <div className="complianceRow">
          <span className="complianceRowLabel">
            {t('home-page.compliance.mandatory.commands-forbidden.title')}
            <HoverExplanationTooltip popoverId="commands-forbidden-tooltip">
              {t('home-page.compliance.mandatory.commands-forbidden.tooltip', { title: item.topic.text })}
            </HoverExplanationTooltip>
          </span>
          <CommandChips commands={item.rejectingCommands} isForbidden />
        </div>
      )}

      {item.complianceExample && (
        <div className="complianceInstructions">
          <span className="complianceInstructionsLabel">{t('home-page.compliance.mandatory.instructions')}</span>
          <div ref={instructionsBodyRef} className={`complianceInstructionsBody${density === 'panel' && !instructionsExpanded ? ' clamped' : ''}`}>
            <Wysiwyg mode="read" value={item.complianceExample} />
          </div>
          {density === 'panel' && isInstructionsOverflowing && (
            <button type="button" className="complianceInstructionsSeeAll" onClick={() => setInstructionsExpanded((current) => !current)}>
              {instructionsExpanded ? t('right-panel.session-compliance.card.seeLessInstructions') : t('right-panel.session-compliance.card.seeAllInstructions')}
            </button>
          )}
        </div>
      )}
    </StyledBox>
  );
}
